import React, { useEffect, useState } from "react";
import ControlLayout from "../components/ControlLayout";
import ConfidenceBadge from "../components/ConfidenceBadge";
import { api } from "../lib/api";
import { Check, X } from "lucide-react";

export default function ControlDashboard() {
  const [feed, setFeed] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [f, r] = await Promise.all([api.get("/attendance/feed?limit=30"), api.get("/attendance/records?limit=20")]);
    setFeed(f.data);
    setRecords(r.data);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 5000);
    return () => clearInterval(i);
  }, []);

  return (
    <ControlLayout title="Live Attendance Feed" subtitle="People Engine · real-time scan stream">
      <div className="grid grid-cols-12 gap-6">
        {/* Scan events (live) */}
        <div className="col-span-12 lg:col-span-5">
          <div className="panel">
            <div className="panel-header">
              <span className="mono-label">Scan events · last 30</span>
              <span className="mono-label text-neutral-400">auto 5s</span>
            </div>
            <div className="divide-y divide-neutral-200 max-h-[72vh] overflow-auto" data-testid="feed-list">
              {loading && <div className="p-6 mono-label">Loading…</div>}
              {!loading && feed.length === 0 && <EmptyFeed />}
              {feed.map((ev) => (
                <div key={ev.id} className="p-3 flex items-center gap-3 hover:bg-neutral-50" data-testid={`feed-row-${ev.id}`}>
                  {ev.employee_photo_url ? (
                    <img src={ev.employee_photo_url} alt="" className="w-10 h-10 object-cover grayscale border border-neutral-200" />
                  ) : (
                    <div className="w-10 h-10 bg-neutral-100 border border-neutral-200" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-sm truncate">{ev.employee_name || "Unknown"}</div>
                      {ev.attendance_action && (
                        <span className="mono-label" style={{ fontSize: 9 }}>{ev.attendance_action.replace("_", " ")}</span>
                      )}
                    </div>
                    <div className="mono-label mt-0.5" style={{ fontSize: 9 }}>
                      {new Date(ev.created_at_iso).toLocaleTimeString("en-GB")} · {ev.decision?.replace(/_/g, " ").toLowerCase()}
                    </div>
                  </div>
                  <ConfidenceBadge tier={ev.confidence_tier} score={ev.confidence_score} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Attendance records */}
        <div className="col-span-12 lg:col-span-7">
          <div className="panel">
            <div className="panel-header">
              <span className="mono-label">Attendance records · latest 20</span>
              <span className="mono-label text-neutral-400">confirmed + review</span>
            </div>
            <table className="w-full text-sm" data-testid="records-table">
              <thead className="bg-[#FAFAFA] border-b border-neutral-200">
                <tr className="text-left">
                  <Th>Employee</Th>
                  <Th>State</Th>
                  <Th>Check-in</Th>
                  <Th>Check-out</Th>
                  <Th>Source</Th>
                  <Th>Confidence</Th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`record-row-${r.id}`}>
                    <Td>
                      <div className="flex items-center gap-2">
                        {r.employee_photo_url && <img src={r.employee_photo_url} alt="" className="w-7 h-7 object-cover grayscale border border-neutral-200" />}
                        <div>
                          <div className="font-semibold">{r.employee_name}</div>
                          <div className="mono-label" style={{ fontSize: 9 }}>{r.employee_code}</div>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <span className={`px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${r.state === "OPEN" ? "border-[#22C55E] text-[#166534] bg-[#DCFCE7]" : "border-neutral-300 bg-neutral-100 text-neutral-700"}`}>
                        {r.state}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs">{r.check_in_at_iso ? new Date(r.check_in_at_iso).toLocaleTimeString("en-GB") : "—"}</Td>
                    <Td className="font-mono text-xs">{r.check_out_at_iso ? new Date(r.check_out_at_iso).toLocaleTimeString("en-GB") : "—"}</Td>
                    <Td>
                      <span className="mono-label" style={{ fontSize: 10 }}>{r.source}</span>
                    </Td>
                    <Td>
                      <ConfidenceBadge tier={r.confidence_tier_out || r.confidence_tier_in} />
                    </Td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center mono-label">No attendance yet. Open the terminal and punch in.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ControlLayout>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 mono-label text-left" style={{ fontSize: 10 }}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function EmptyFeed() {
  return (
    <div className="p-8 text-center">
      <div className="mono-label">Waiting for scans</div>
      <p className="text-sm text-neutral-500 mt-2">Open <code className="font-mono">/terminal</code> and run a scan.</p>
    </div>
  );
}

export { ControlDashboard };
